import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import { sortChatsByLastMessage } from 'utils/chats'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { ChatRow } from 'components/chats/ChatRow'
import { OdinListEmpty } from '@/components/ui/odin/odin-list-empty'
import { ChatBubbleSparklesSvg } from 'components/ui/svg/ChatBubbleSparklesSvg'
import { ChatPageSidebarFooter } from 'components/chat-page/ChatPageSidebarFooter'
import { messagesActions, messagesSelectors } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { useGetSetting } from 'api/settings'
import { useUserChatListSocket } from 'hooks/useUserChatListSocket'

export const ChatPageSidebar = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { setOpenMobile } = useSidebar()
  const { setting, title } = useGetSetting()
  const chatsMap = useSelector(messagesSelectors.chats)
  const currentChatId = useSelector(messagesSelectors.chatId)
  const currentUserId = useSelector(settingsSelectors.userId)

  useUserChatListSocket(currentUserId ? String(currentUserId) : undefined)

  const sortedChats = useMemo(() => sortChatsByLastMessage(chatsMap), [chatsMap])

  const onClickNewChat = () => {
    dispatch(messagesActions.setChatId(undefined))
    setOpenMobile(false)
  }

  return (
    <Sidebar collapsible="offcanvas" className="border-sidebar-border">
      <SidebarHeader className="gap-0 p-0">
        <div className="flex items-center gap-2.5 px-4 pt-4 pb-2">
          {setting?.logo?.full && (
            <img
              src={setting?.logo?.thumbnail?.medium ?? setting?.logo?.full}
              alt="Logo"
              className="h-9 w-9 rounded-lg object-cover"
            />
          )}
          <div className="truncate text-base font-bold text-foreground">{title}</div>
        </div>

        <div className="px-3 pt-2 pb-3">
          <OdinButton onClick={onClickNewChat} className="w-full">
            <OdinIcon icon={['far', 'plus']} className="size-3.5" />
            <span>{t('openNewChat')}</span>
          </OdinButton>
        </div>
      </SidebarHeader>

      <SidebarContent className="scrollbar-hidden px-2">
        {isEmpty(sortedChats) ? (
          <OdinListEmpty image={<ChatBubbleSparklesSvg />} title={t('emptyChats')} description={t('emptyChatsHint')} />
        ) : (
          <SidebarMenu className="gap-0.5" onClick={() => setOpenMobile(false)}>
            {sortedChats.map((chat) => (
              <SidebarMenuItem key={chat._id}>
                <ChatRow chat={chat} selected={chat._id === currentChatId} />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        )}
      </SidebarContent>

      <SidebarFooter className="p-0">
        <ChatPageSidebarFooter />
      </SidebarFooter>
    </Sidebar>
  )
}
